import { useState } from "react";
import { useData } from "../../context/DataContext";
import { Card, CardHead, Btn, Label, TextInput, NumberInput, DateInput, TextArea, NativeSelect, TableWrap, Th, Td, EmptyState, IconBtn } from "../kit";
import { fmtINR, uid, todayStr, nextInvoiceNumber, computeInvoiceTotals, INCOME_CATEGORIES } from "../../lib/finance";
import { generateInvoicePdf } from "../../lib/invoicePdf";
import { toast } from "sonner";
import { Download, Trash2, FileText } from "lucide-react";

export default function Invoices() {
  const { invoices, saveInvoices, entries, saveEntries, pipeline, savePipeline, businessProfile, saveBusinessProfile } = useData();
  const [showBiz, setShowBiz] = useState(false);
  const [biz, setBiz] = useState(businessProfile);
  const [items, setItems] = useState([{ id: uid(), description: "", price: 0, qty: 1, discount: 0, igstRate: 0 }]);
  const [inv, setInv] = useState({ billTo: "", address: "", gst: "", number: nextInvoiceNumber(invoices), date: todayStr(), dueDate: "", logIncome: true, incomeCat: "Brand Collabs" });
  const setI = (k) => (e) => setInv({ ...inv, [k]: e.target.value });

  const totals = computeInvoiceTotals(items);

  const saveBiz = async () => { await saveBusinessProfile(biz); setShowBiz(false); toast.success("Business details saved"); };
  const updItem = (id, k, v) => setItems(items.map((r) => (r.id === id ? { ...r, [k]: k === "description" ? v : parseFloat(v) || 0 } : r)));
  const addItem = () => setItems([...items, { id: uid(), description: "", price: 0, qty: 1, discount: 0, igstRate: 0 }]);
  const rmItem = (id) => setItems(items.filter((r) => r.id !== id));

  const resetForm = () => {
    setItems([{ id: uid(), description: "", price: 0, qty: 1, discount: 0, igstRate: 0 }]);
    setInv({ billTo: "", address: "", gst: "", number: nextInvoiceNumber([...invoices, { number: inv.number }]), date: todayStr(), dueDate: "", logIncome: true, incomeCat: "Brand Collabs" });
  };

  const generate = async () => {
    if (!inv.billTo.trim()) return toast.error("Enter who you're billing");
    if (!items.length || items.every((r) => !r.description && !r.price)) return toast.error("Add at least one line item");
    const cleanItems = items.map((r) => ({ description: r.description, price: r.price, qty: r.qty, discount: r.discount, igstRate: r.igstRate }));
    const record = { id: uid(), number: inv.number, billTo: inv.billTo.trim(), billToAddress: inv.address.trim(), billToGst: inv.gst.trim(), date: inv.date, dueDate: inv.dueDate, items: cleanItems, total: totals.total };

    generateInvoicePdf(record, businessProfile);
    await saveInvoices([...invoices, record]);

    if (inv.logIncome && totals.total > 0) {
      if (inv.dueDate) {
        await savePipeline([...pipeline, { id: uid(), brand: inv.billTo.trim(), expectedAmount: totals.total, expectedDate: inv.dueDate, status: "Verbally Confirmed", notes: `Invoice #${inv.number} — awaiting payment by due date`, createdDate: todayStr(), invoiceNumber: inv.number }]);
        toast.success(`Invoice #${inv.number} generated — added to Pipeline (due ${inv.dueDate})`);
      } else {
        await saveEntries([...entries, { id: uid(), type: "income", category: inv.incomeCat, amount: totals.total, description: `${inv.billTo.trim()} — Invoice #${inv.number}`, date: inv.date }]);
        toast.success(`Invoice #${inv.number} generated and logged to Ledger`);
      }
    } else {
      toast.success(`Invoice #${inv.number} generated and downloaded`);
    }
    resetForm();
  };

  const redownload = (record) => generateInvoicePdf(record, businessProfile);
  const delInv = async (id) => { if (!window.confirm("Delete this invoice record? (Any linked income entry stays — remove from Ledger separately.)")) return; await saveInvoices(invoices.filter((i) => i.id !== id)); toast.success("Invoice record deleted"); };

  const issued = invoices.slice().sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="space-y-6">
      <Card>
        <CardHead title="Your business details" subtitle="Used on every invoice you generate" right={<Btn variant="outline" onClick={() => { setBiz(businessProfile); setShowBiz(!showBiz); }} data-testid="toggle-biz">✎ Edit</Btn>} />
        <div className="px-5 pb-5 pt-2 text-sm text-slate-600">
          <p className="font-semibold text-slate-900">{businessProfile.name || "—"}</p>
          <p className="whitespace-pre-line text-xs mt-1">{businessProfile.address}</p>
        </div>
        {showBiz && (
          <div className="px-5 pb-5 grid sm:grid-cols-2 gap-x-4">
            <div>
              <Label>Business name</Label><TextInput value={biz.name || ""} onChange={(e) => setBiz({ ...biz, name: e.target.value })} />
              <Label>UDYAM / registration no.</Label><TextInput value={biz.udyam || ""} onChange={(e) => setBiz({ ...biz, udyam: e.target.value })} />
              <Label>Address</Label><TextArea rows={4} value={biz.address || ""} onChange={(e) => setBiz({ ...biz, address: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label><TextInput value={biz.email || ""} onChange={(e) => setBiz({ ...biz, email: e.target.value })} />
              <Label>Phone</Label><TextInput value={biz.phone || ""} onChange={(e) => setBiz({ ...biz, phone: e.target.value })} />
              <Label>Bank account name</Label><TextInput value={biz.bankAccountName || ""} onChange={(e) => setBiz({ ...biz, bankAccountName: e.target.value })} />
              <Label>Bank account number</Label><TextInput value={biz.bankAccountNumber || ""} onChange={(e) => setBiz({ ...biz, bankAccountNumber: e.target.value })} />
              <Label>Bank name</Label><TextInput value={biz.bankName || ""} onChange={(e) => setBiz({ ...biz, bankName: e.target.value })} />
              <Label>IFSC</Label><TextInput value={biz.ifsc || ""} onChange={(e) => setBiz({ ...biz, ifsc: e.target.value })} />
            </div>
            <div className="sm:col-span-2"><Btn className="mt-4" onClick={saveBiz} data-testid="save-biz">Save business details</Btn></div>
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="text-lg font-semibold text-slate-900 font-head">Create invoice</h2>
        <div className="grid sm:grid-cols-2 gap-x-4">
          <div>
            <Label>Bill to (client / brand)</Label><TextInput value={inv.billTo} onChange={setI("billTo")} placeholder="e.g. Trigg Ventures" data-testid="inv-billto" />
            <Label>Bill-to address</Label><TextArea rows={3} value={inv.address} onChange={setI("address")} placeholder="Street, City, State" />
            <Label>Bill-to GST (optional)</Label><TextInput value={inv.gst} onChange={setI("gst")} />
          </div>
          <div>
            <Label>Invoice number</Label><TextInput value={inv.number} onChange={setI("number")} data-testid="inv-number" />
            <Label>Invoice date</Label><DateInput value={inv.date} onChange={setI("date")} />
            <Label>Due date (optional)</Label><DateInput value={inv.dueDate} onChange={setI("dueDate")} />
            <p className="text-xs text-muted-foreground mt-1">Blank = already paid (logs to Ledger). Filled = goes to Pipeline until confirmed.</p>
          </div>
        </div>

        <Label>Line items</Label>
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-2 items-center">
              <input className="col-span-5 rounded-lg border border-slate-200 px-2 py-1.5 text-sm" placeholder="Description" value={r.description} onChange={(e) => updItem(r.id, "description", e.target.value)} />
              <input type="number" className="col-span-2 rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-right" placeholder="Price" value={r.price} onChange={(e) => updItem(r.id, "price", e.target.value)} />
              <input type="number" className="col-span-1 rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-right" value={r.qty} onChange={(e) => updItem(r.id, "qty", e.target.value)} />
              <input type="number" className="col-span-2 rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-right" placeholder="Disc" value={r.discount} onChange={(e) => updItem(r.id, "discount", e.target.value)} />
              <input type="number" className="col-span-1 rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-right" placeholder="IGST%" value={r.igstRate} onChange={(e) => updItem(r.id, "igstRate", e.target.value)} />
              <div className="col-span-1 text-right"><IconBtn onClick={() => rmItem(r.id)} className="hover:text-rose-600"><Trash2 className="h-4 w-4" /></IconBtn></div>
            </div>
          ))}
        </div>
        <Btn variant="outline" className="mt-2" onClick={addItem}>+ Add line item</Btn>

        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm space-y-1">
          <Row l="Basis" v={fmtINR(totals.basis)} />
          <Row l="Discount" v={totals.discount ? "-" + fmtINR(totals.discount) : "-"} />
          <Row l="Net amount" v={fmtINR(totals.net)} />
          <Row l="IGST" v={totals.igst ? fmtINR(totals.igst) : "-"} />
          <div className="flex justify-between pt-2 mt-1 border-t border-slate-200 font-bold text-slate-900"><span>TOTAL DUE (INR)</span><span className="tnum">{fmtINR(totals.total)}</span></div>
        </div>

        <label className="flex items-center gap-2 mt-4 text-sm text-slate-600">
          <input type="checkbox" checked={inv.logIncome} onChange={(e) => setInv({ ...inv, logIncome: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
          Also track this amount when I generate the invoice
        </label>
        {inv.logIncome && !inv.dueDate && (
          <div className="mt-2">
            <Label>Income category (when logged to Ledger)</Label>
            <NativeSelect value={inv.incomeCat} onChange={setI("incomeCat")}>
              {INCOME_CATEGORIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </NativeSelect>
          </div>
        )}
        <Btn className="mt-4 w-full" onClick={generate} data-testid="generate-pdf-invoice-button"><FileText className="h-4 w-4" /> Generate invoice PDF</Btn>
      </Card>

      <Card>
        <CardHead title="Issued invoices" />
        <div className="mt-4">
          {issued.length === 0 ? <EmptyState>No invoices issued yet.</EmptyState> : (
            <TableWrap>
              <thead><tr><Th>Inv #</Th><Th>Bill To</Th><Th>Date</Th><Th className="text-right">Amount</Th><Th></Th></tr></thead>
              <tbody>
                {issued.map((i) => (
                  <tr key={i.id} className="hover:bg-slate-50">
                    <Td className="font-medium">{i.number}</Td>
                    <Td>{i.billTo}</Td>
                    <Td className="text-slate-500">{i.date}</Td>
                    <Td className="text-right tnum text-emerald-600 font-semibold">{fmtINR(i.total)}</Td>
                    <Td><div className="flex gap-1"><IconBtn title="Download" onClick={() => redownload(i)} className="text-primary"><Download className="h-4 w-4" /></IconBtn><IconBtn title="Delete" onClick={() => delInv(i.id)} className="hover:text-rose-600"><Trash2 className="h-4 w-4" /></IconBtn></div></Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </div>
        <div className="h-4" />
      </Card>
    </div>
  );
}

function Row({ l, v }) { return <div className="flex justify-between text-slate-600"><span>{l}</span><span className="tnum">{v}</span></div>; }
